import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  message,
  Tabs,
  Row,
  Col,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import PublicWebContentService from '../services/publicWebContentService';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const PublicWebContentManager = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [stats, setStats] = useState({});
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('manage');

  const pages = [
    'home',
    'about',
    'services',
    'products',
    'contact',
    'how-it-works',
    'testimonials',
    'faq',
    'global',
  ];

  const sections = [
    'hero',
    'stats',
    'mission',
    'vision',
    'story',
    'values',
    'team',
    'custom_solutions',
    'how_it_works',
    'contact_info',
    'getting_started',
    'general',
    'services',
    'footer',
  ];

  const contentTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'number', label: 'Number' },
    { value: 'image', label: 'Image URL' },
    { value: 'video', label: 'Video URL' },
    { value: 'html', label: 'HTML' },
  ];

  useEffect(() => {
    loadContent();
    loadStats();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = await PublicWebContentService.getAllContent(token);
      setContent(data);
    } catch (error) {
      message.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await PublicWebContentService.getContentStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleEdit = (record) => {
    setEditingContent(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleAdd = () => {
    setEditingContent(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleSave = async (values) => {
    try {
      const token = localStorage.getItem('token');
      await PublicWebContentService.upsertContent(values, token);
      message.success('Content saved successfully');
      setModalVisible(false);
      loadContent();
      loadStats();
    } catch (error) {
      message.error('Failed to save content');
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await PublicWebContentService.deleteContent(id, token);
      message.success('Content deleted successfully');
      loadContent();
      loadStats();
    } catch (error) {
      message.error('Failed to delete content');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await PublicWebContentService.toggleContentStatus(id, token);
      message.success('Status updated successfully');
      loadContent();
      loadStats();
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const columns = [
    {
      title: 'Page',
      dataIndex: 'page',
      key: 'page',
      render: (page) => <Tag color="blue">{page}</Tag>,
      filters: pages.map((page) => ({ text: page, value: page })),
      onFilter: (value, record) => record.page === value,
    },
    {
      title: 'Section',
      dataIndex: 'section',
      key: 'section',
      render: (section) => <Tag color="green">{section}</Tag>,
      filters: sections.map((section) => ({ text: section, value: section })),
      onFilter: (value, record) => record.section === value,
    },
    {
      title: 'Content Key',
      dataIndex: 'content_key',
      key: 'content_key',
      render: (key) => <code>{key}</code>,
    },
    {
      title: 'Content Type',
      dataIndex: 'content_type',
      key: 'content_type',
      render: (type) => <Tag color="orange">{type}</Tag>,
    },
    {
      title: 'Content Value',
      dataIndex: 'content_value',
      key: 'content_value',
      ellipsis: true,
      render: (value, record) => {
        if (record.content_type === 'textarea') {
          return <span title={value}>{value.substring(0, 50)}...</span>;
        }
        return <span title={value}>{value}</span>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleStatus(record.id)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Sort Order',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const renderContentPreview = () => {
    const groupedContent = content.reduce((acc, item) => {
      if (!acc[item.page]) acc[item.page] = {};
      if (!acc[item.page][item.section]) acc[item.page][item.section] = [];
      acc[item.page][item.section].push(item);
      return acc;
    }, {});

    return (
      <div>
        {Object.entries(groupedContent).map(([page, sections]) => (
          <Card key={page} title={`Page: ${page}`} style={{ marginBottom: 16 }}>
            {Object.entries(sections).map(([section, items]) => (
              <div key={section} style={{ marginBottom: 16 }}>
                <Title level={5}>{section}</Title>
                <Row gutter={[16, 16]}>
                  {items
                    .filter((item) => item.is_active)
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((item) => (
                      <Col span={8} key={item.id}>
                        <Card size="small">
                          <div style={{ fontWeight: 'bold' }}>{item.content_key}</div>
                          <div style={{ color: '#666' }}>
                            {item.content_type === 'textarea'
                              ? item.content_value.substring(0, 100) + '...'
                              : item.content_value}
                          </div>
                        </Card>
                      </Col>
                    ))}
                </Row>
              </div>
            ))}
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Public Web Content Manager</Title>
      <Paragraph>
        Manage all content displayed on the public website. Edit text, update statistics, and
        control what visitors see.
      </Paragraph>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Overview" key="overview">
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Content Items"
                  value={stats.total_content || 0}
                  prefix={<EditOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Active Content"
                  value={stats.active_content || 0}
                  prefix={<EyeOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Pages"
                  value={stats.pages?.length || 0}
                  prefix={<PlusOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Sections"
                  value={stats.sections?.length || 0}
                  prefix={<PlusOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Card
            title="Content Preview"
            extra={<Button icon={<ReloadOutlined />} onClick={loadContent} />}
          >
            {renderContentPreview()}
          </Card>
        </TabPane>

        <TabPane tab="Manage Content" key="manage">
          <Card
            title="Content Management"
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadContent}>
                  Refresh
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                  Add Content
                </Button>
              </Space>
            }
          >
            <Table
              columns={columns}
              dataSource={content}
              loading={loading}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </Card>
        </TabPane>
      </Tabs>

      <Modal
        title={editingContent ? 'Edit Content' : 'Add Content'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} onClick={() => form.submit()}>
            Save
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="page"
            label="Page"
            rules={[{ required: true, message: 'Please select a page' }]}
          >
            <Select placeholder="Select a page">
              {pages.map((page) => (
                <Select.Option key={page} value={page}>
                  {page}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="section"
            label="Section"
            rules={[{ required: true, message: 'Please select a section' }]}
          >
            <Select placeholder="Select a section">
              {sections.map((section) => (
                <Select.Option key={section} value={section}>
                  {section}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="content_key"
            label="Content Key"
            rules={[{ required: true, message: 'Please enter a content key' }]}
          >
            <Input placeholder="e.g., headline, subheadline, description" />
          </Form.Item>

          <Form.Item
            name="content_type"
            label="Content Type"
            rules={[{ required: true, message: 'Please select a content type' }]}
          >
            <Select placeholder="Select content type">
              {contentTypes.map((type) => (
                <Select.Option key={type.value} value={type.value}>
                  {type.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.content_type !== currentValues.content_type
            }
          >
            {({ getFieldValue }) => {
              const contentType = getFieldValue('content_type');
              return (
                <Form.Item
                  name="content_value"
                  label="Content Value"
                  rules={[{ required: true, message: 'Please enter content value' }]}
                >
                  {contentType === 'textarea' ? (
                    <TextArea rows={4} placeholder="Enter content value" />
                  ) : (
                    <Input placeholder="Enter content value" />
                  )}
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item name="sort_order" label="Sort Order">
            <Input type="number" placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PublicWebContentManager;
